import { RefObject, useEffect, useState } from 'react'
import { SectionDef, SectionKey } from './sections'

interface Options {
  sections: SectionDef[]
  scrollRootRef: RefObject<HTMLElement>
  ready?: boolean
}

export const useActiveSection = ({
  sections,
  scrollRootRef,
  ready = true,
}: Options): SectionKey => {
  const [active, setActive] = useState<SectionKey>(() => {
    const hash = typeof window !== 'undefined' ? window.location.hash.slice(1) : ''
    const match = sections.find(s => s.key === hash)
    return match?.key ?? sections[0].key
  })

  useEffect(() => {
    if (!ready) return
    const root = scrollRootRef.current
    if (!root) return

    const initialHash = window.location.hash.slice(1)
    const initialMatch = sections.find(s => s.key === initialHash)
    if (initialMatch) {
      const el = document.getElementById(initialMatch.key)
      if (el) el.scrollIntoView({ block: 'start' })
    }

    const visible = new Map<SectionKey, number>()

    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          const key = (entry.target as HTMLElement).dataset.sectionKey as SectionKey | undefined
          if (!key) continue
          if (entry.isIntersecting) {
            visible.set(key, entry.boundingClientRect.top)
          } else {
            visible.delete(key)
          }
        }
        if (visible.size === 0) return
        let topKey: SectionKey | undefined
        let topVal = Infinity
        visible.forEach((v, k) => {
          if (v < topVal) {
            topVal = v
            topKey = k
          }
        })
        if (!topKey) return
        setActive(prev => (prev === topKey ? prev : topKey!))
      },
      {
        root,
        threshold: [0, 0.25, 0.5],
        rootMargin: '-10% 0px -60% 0px',
      }
    )

    for (const s of sections) {
      const el = document.getElementById(s.key)
      if (el) observer.observe(el)
    }

    return () => observer.disconnect()
  }, [sections, scrollRootRef, ready])

  return active
}
